// ToolsPage.tsx — Shared tools → Findings (`/shared-tools/findings`).
//
// The system-wide duplicate-code dashboard — pattern families over the persisted
// cross-repo scan, each expandable into member clusters with exact file spans,
// plus per-cluster Ignore (durable feedback) and Propose (files a registry
// proposal + build task) actions. The "Run live scan" button starts a
// server-side scan of every split family (the server also re-runs it on a
// schedule); progress streams in real time over
// the `tool_finder.scan` WebSocket scope into the progress panel.
//
// A pattern family names the repos it spans, so the family scope keeps the
// ones that reach into it: the scan endpoint takes no family, and the repo→
// family map the shell already has answers it here. The rest are counted under
// the cards.

import { Radar, Sparkles } from 'lucide-react';

import { PageHeader, queryFreshness } from '../components/pageHeader';
import { ErrorState, LoadingState } from '../components/state';
import {
  useToolFinderDashboard,
  useToolFinderScan,
} from '../hooks/useToolFinder';
import {
  PatternFamilyCard,
  ScanProgressPanel,
  formatCount,
  formatScannedAt,
} from './tools';
import { OtherFamilies } from '../components/family/OtherFamilies';
import { ScopedEmptyState } from '../components/family/ScopedEmptyState';
import { useRepoFamilyScope } from '../components/family/useRepoFamilyScope';

import './page.css';
import './ToolsPage.css';
import { SharedToolsTabs } from './sharedTools/SharedToolsTabs';
import { usePageTitle } from '../hooks/usePageTitle';

export function ToolsPage(): JSX.Element {
  usePageTitle('Shared tools');
  const dashboard = useToolFinderDashboard();
  const scan = useToolFinderScan();
  const scope = useRepoFamilyScope();
  // A family stays while any repo it spans is in scope: a cluster shared with
  // another family is this family's problem too.
  const families = scope.rowsByRepos(
    dashboard.data?.families ?? [],
    (family) => family.repos
  );
  // A failed scan is an error with a reason, not an empty result: it gets one
  // clear state, and the "0 clusters in 0 families" statistics stay out of it.
  const scanFailed = !scan.isRunning && scan.status?.phase === 'failed';
  const showProgress =
    scan.isRunning || (scan.status?.phase === 'completed' && scan.feed.length > 0);

  return (
    <div className="page page--wide" data-testid="tools-page">
      <PageHeader
        title="Shared tools"
        className="tools-header"
        testId="shared-tools-header"
        freshness={queryFreshness(dashboard)}
        subtitle="Duplicated code across every split family, ranked by the lines a shared tool would save. Propose a cluster to send it to Proposals; the scan re-runs on its own every day."
        actions={
          <div className="tools-header__scan">
            {dashboard.data && !scanFailed ? (
              <span className="tools-header__meta">
                last scan: {formatScannedAt(dashboard.data.scan.scanned_at)} ·{' '}
                {formatCount(dashboard.data.cluster_count)} clusters in{' '}
                {formatCount(dashboard.data.family_count)} families · ~
                {formatCount(dashboard.data.candidate_loc_saved)} candidate LOC
              </span>
            ) : null}
            <button
              type="button"
              className="tools-button tools-button--primary"
              data-testid="run-scan-button"
              disabled={scan.isRunning || scan.isStarting}
              onClick={scan.start}
            >
              <Radar size={14} aria-hidden="true" />
              {scan.isRunning ? 'Scanning…' : 'Run live scan'}
            </button>
          </div>
        }
      >
        <SharedToolsTabs />
        {scan.startError ? (
          <p className="tools-scan__error">{scan.startError.message}</p>
        ) : null}
      </PageHeader>

      {showProgress && scan.status ? (
        <ScanProgressPanel status={scan.status} feed={scan.feed} />
      ) : null}
      {scanFailed ? (
        <div data-testid="tools-scan-failed">
          <ErrorState
            title="The last scan failed."
            description={
              scan.status?.error
                ? `${scan.status.error}. Nothing below reflects that scan; run it again once the cause is fixed.`
                : 'The scan did not finish. Run it again; if it keeps failing, check the server log.'
            }
          />
        </div>
      ) : null}

      <section className="tools-dashboard" aria-label="Pattern families">
        {dashboard.isPending ? (
          <LoadingState title="Loading the duplicate-code dashboard…" variant="message" />
        ) : dashboard.isError ? (
          <ErrorState
            title="Could not load the dashboard."
            error={dashboard.error}
          />
        ) : families.rows.length === 0 ? (
          scanFailed ? null : (
            <>
              <ScopedEmptyState
                icon={Sparkles}
                title="No cross-repo clusters yet."
                description="Run a live scan to mine every split family for duplicated code worth extracting into shared tools."
              />
              <OtherFamilies count={families.elsewhere} noun="pattern families" />
            </>
          )
        ) : (
          <>
            <div className="tools-dashboard__families">
              {families.rows.map((family) => (
                <PatternFamilyCard key={family.family_id} family={family} />
              ))}
            </div>
            <OtherFamilies count={families.elsewhere} noun="pattern families" />
          </>
        )}
      </section>
    </div>
  );
}
